import React, { useEffect, useState, useRef } from "react";
import "dhtmlx-gantt/codebase/dhtmlxgantt.css";
import "dhtmlx-gantt";
import { ChevronDown, ChevronUp } from "lucide-react";

interface MultiSelectDropdownProps {
  title: string;
  options: string[];
  labels?: Record<string, string>;
  selected: string[];
  setSelected: (val: string[]) => void;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  title,
  options,
  labels,
  selected,
  setSelected,
}) => {
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleOption = (value: string) => {
    if (selected.includes(value)) {
      setSelected(selected.filter((x) => x !== value));
    } else {
      setSelected([...selected, value]);
    }
  };

  const selectAll = () => setSelected([...options]);
  const clearAll = () => setSelected([]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div className="relative inline-block text-sm" ref={dropdownRef}>
      <button
        onClick={() => setOpen((prev) => !prev)}
        className={`
          flex items-center justify-between gap-2 px-3 py-1
          border rounded-lg transition-all duration-200 ease-in-out
          min-w-[160px] text-left h-8
          ${open 
            ? "border-blue-500 ring-2 ring-blue-100 bg-blue-50" 
            : "border-gray-300 hover:border-gray-400 hover:bg-gray-50"
          }
          ${selected.length > 0 ? "bg-blue-50 border-blue-200" : "bg-white"}
        `}
      >
        <span className="flex items-center gap-2 truncate">
          <span className="font-medium text-gray-700">{title}</span>
          {selected.length > 0 && (
            <span className="flex items-center justify-center w-5 h-5 text-xs font-semibold text-white bg-blue-500 rounded-full">
              {selected.length}
            </span>
          )}
        </span>
        {open ? (
          <ChevronUp className="w-4 h-4 text-gray-500 flex-shrink-0" />
        ) : (
          <ChevronDown className="w-4 h-4 text-gray-500 flex-shrink-0" />
        )}
      </button>

      {open && (
        <div className="
          absolute top-full left-0 z-50 mt-1 
          bg-white border border-gray-200 rounded-lg shadow-lg 
          w-48 min-h-70 overflow-hidden
          animate-in fade-in-0 zoom-in-95
        ">
          {/* Header with actions */}
          <div className="flex items-center justify-between p-2 border-b border-gray-100 bg-gray-50">
            <button
              onClick={selectAll}
              className="px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
            >
              Select All
            </button>
            <button
              onClick={clearAll}
              className="px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
            >
              Clear
            </button>
          </div>

          {/* Options list */}
          <div className="overflow-y-auto max-h-44">
            {options.map((option) => (
              <label
                key={option}
                className={`
                  flex items-center gap-3 px-3 py-2 cursor-pointer transition-colors
                  hover:bg-gray-50 active:bg-gray-100
                  ${selected.includes(option) ? "bg-blue-50 hover:bg-blue-100" : ""}
                `}
              >
                <div className="relative flex items-center">
                  <input
                    type="checkbox"
                    checked={selected.includes(option)}
                    onChange={() => toggleOption(option)}
                    className="
                      w-4 h-4 text-blue-600 bg-white border-gray-300 rounded 
                      focus:ring-blue-500 focus:ring-2 focus:ring-offset-1
                      transition-colors cursor-pointer
                    "
                  />
                </div>
                <span className={`
                  text-sm font-normal select-none
                  ${selected.includes(option) ? "text-blue-700" : "text-gray-700"}
                `}>
                  {labels ? labels[option] : option}
                </span>
              </label>
            ))}
          </div>

          {/* Selected count footer */}
          {selected.length > 0 && (
            <div className="px-3 py-2 border-t border-gray-100 bg-gray-50">
              <p className="text-xs text-gray-600 ">
                {selected.length} {selected.length === 1 ? 'item' : 'items'} selected
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;